/**
 * VisitorJourneyMatrix component — a matrix view of visitor reading journeys.
 *
 * Rows = visitors (anonymous hashes, or labeled names)
 * Columns = enter page + each essay
 * Cells = time spent, visit count, revisits
 *
 * Features:
 * - Click visitor hash to assign a friendly label
 * - Color-coded cells by engagement depth
 * - Date range picker
 * - Sortable by total time, last seen, etc.
 *
 * Called by:
 * - AnalyticsPage.tsx Visitor Journeys tab
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Typography,
  Spin,
  Empty,
  DatePicker,
  Segmented,
  Tooltip,
} from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getAnalyticsVisitors } from '../../services/api';
import type { VisitorJourney } from '../../services/api';
import LabelEditor from './LabelEditor';

dayjs.extend(relativeTime);

const { Text } = Typography;
const { RangePicker } = DatePicker;

type RangePreset = '7d' | '30d' | '90d' | 'all' | 'custom';

function formatDuration(seconds: number): string {
  if (seconds === 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

/** Color intensity based on duration in seconds */
function cellColor(duration: number, visits: number): string {
  if (visits === 0) return 'transparent';
  if (duration === 0 && visits > 0) return 'rgba(24, 144, 255, 0.15)'; // visited but no duration data
  if (duration < 30) return 'rgba(24, 144, 255, 0.2)';
  if (duration < 120) return 'rgba(24, 144, 255, 0.35)';
  if (duration < 300) return 'rgba(82, 196, 26, 0.35)';
  return 'rgba(82, 196, 26, 0.5)';
}

// =============================================================================
// Component
// =============================================================================

function VisitorJourneyMatrix() {
  const [rangePreset, setRangePreset] = useState<RangePreset>('30d');
  const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const { from, to } = useMemo(() => {
    if (rangePreset === 'custom' && customRange) {
      return {
        from: customRange[0].format('YYYY-MM-DD'),
        to: customRange[1].format('YYYY-MM-DD'),
      };
    }
    if (rangePreset === 'all') {
      return { from: '2020-01-01', to: dayjs().format('YYYY-MM-DD') };
    }
    const days = rangePreset === '7d' ? 7 : rangePreset === '90d' ? 90 : 30;
    return {
      from: dayjs().subtract(days, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
    };
  }, [rangePreset, customRange]);

  const { data: response, isLoading } = useQuery({
    queryKey: ['analyticsVisitors', from, to],
    queryFn: () => getAnalyticsVisitors(from, to),
    refetchInterval: 60000,
  });

  const visitors = response?.visitors;
  const essayOrder = response?.essayOrder || [];

  // Derive columns: non-essay pages in a fixed order, then essays in series order
  const columns = useMemo(() => {
    if (!visitors) return [];
    const slugSet = new Set<string>();
    const pathSet = new Set<string>();

    for (const v of visitors) {
      for (const p of v.pages) {
        if (p.essaySlug) {
          slugSet.add(p.essaySlug);
        } else {
          pathSet.add(p.path);
        }
      }
    }

    // Non-essay pages: put "/" first, then "/home", then others
    const nonEssay = Array.from(pathSet).sort((a, b) => {
      if (a === '/') return -1;
      if (b === '/') return 1;
      if (a === '/home') return -1;
      if (b === '/home') return 1;
      return a.localeCompare(b);
    });

    // Essays: use series order, then append any extras alphabetically
    const orderedSlugs: string[] = [];
    for (const slug of essayOrder) {
      if (slugSet.has(slug)) {
        orderedSlugs.push(slug);
        slugSet.delete(slug);
      }
    }
    // Any slugs not in the series order go at the end
    const remaining = Array.from(slugSet).sort();
    orderedSlugs.push(...remaining);

    return [
      ...nonEssay.map(p => ({ key: p, label: p === '/' ? 'Enter' : p, isEssay: false })),
      ...orderedSlugs.map(s => ({ key: s, label: s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '), isEssay: true })),
    ];
  }, [visitors, essayOrder]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!visitors || visitors.length === 0) {
    return (
      <div>
        <DateControls
          rangePreset={rangePreset}
          setRangePreset={setRangePreset}
          customRange={customRange}
          setCustomRange={setCustomRange}
          from={from}
          to={to}
        />
        <Empty description="No visitor data for this period" style={{ marginTop: '48px' }} />
      </div>
    );
  }

  return (
    <div>
      <DateControls
        rangePreset={rangePreset}
        setRangePreset={setRangePreset}
        customRange={customRange}
        setCustomRange={setCustomRange}
        from={from}
        to={to}
      />

      <Card
        style={{ background: '#1a1a1a', borderColor: '#333', marginTop: '16px', overflow: 'auto' }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              minWidth: `${200 + columns.length * 120}px`,
              borderCollapse: 'collapse',
              fontSize: '13px',
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    background: '#1a1a1a',
                    padding: '10px 12px',
                    borderBottom: '1px solid #333',
                    borderRight: '1px solid #333',
                    textAlign: 'left',
                    color: '#999',
                    fontWeight: 500,
                    minWidth: '200px',
                  }}
                >
                  Visitor
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    style={{
                      padding: '10px 8px',
                      borderBottom: '1px solid #333',
                      borderRight: '1px solid #333',
                      textAlign: 'center',
                      color: col.isEssay ? '#d9d9d9' : '#999',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Tooltip title={col.key}>
                      <span>{col.label.replace(/^\//, '') || 'Home'}</span>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visitors.map((visitor) => (
                <VisitorRow key={visitor.visitorHash} visitor={visitor} columns={columns} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Legend */}
      <div style={{ marginTop: '12px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>Engagement:</Text>
        {[
          { label: 'Visited', color: 'rgba(24, 144, 255, 0.15)' },
          { label: '<30s', color: 'rgba(24, 144, 255, 0.2)' },
          { label: '<2m', color: 'rgba(24, 144, 255, 0.35)' },
          { label: '<5m', color: 'rgba(82, 196, 26, 0.35)' },
          { label: '5m+', color: 'rgba(82, 196, 26, 0.5)' },
        ].map((item) => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '16px', height: '16px', background: item.color, borderRadius: '2px', border: '1px solid #333' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>{item.label}</Text>
          </div>
        ))}
        <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
          Click a visitor name to label them
        </Text>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function DateControls({
  rangePreset,
  setRangePreset,
  customRange,
  setCustomRange,
  from,
  to,
}: {
  rangePreset: RangePreset;
  setRangePreset: (v: RangePreset) => void;
  customRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
  setCustomRange: (v: [dayjs.Dayjs, dayjs.Dayjs] | null) => void;
  from: string;
  to: string;
}) {
  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
      <Segmented
        value={rangePreset}
        onChange={(val) => setRangePreset(val as RangePreset)}
        options={[
          { label: '7 days', value: '7d' },
          { label: '30 days', value: '30d' },
          { label: '90 days', value: '90d' },
          { label: 'All time', value: 'all' },
          { label: 'Custom', value: 'custom' },
        ]}
      />
      {rangePreset === 'custom' && (
        <RangePicker
          value={customRange}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setCustomRange([dates[0], dates[1]]);
            }
          }}
        />
      )}
      <Text type="secondary" style={{ marginLeft: 'auto' }}>
        {dayjs(from).format('MMM D')} - {dayjs(to).format('MMM D, YYYY')}
      </Text>
    </div>
  );
}

function VisitorRow({
  visitor,
  columns,
}: {
  visitor: VisitorJourney;
  columns: { key: string; label: string; isEssay: boolean }[];
}) {
  // Build a lookup: page key -> page data
  const pageMap = useMemo(() => {
    const m = new Map<string, { views: number; totalDuration: number; avgDuration: number; videoProgress: number | null; scrollDepth: number | null; firstSeen: string; lastSeen: string }>();
    for (const p of visitor.pages) {
      const key = p.essaySlug || p.path;
      m.set(key, p);
    }
    return m;
  }, [visitor.pages]);

  return (
    <tr>
      <td
        style={{
          position: 'sticky',
          left: 0,
          zIndex: 1,
          background: '#1a1a1a',
          padding: '8px 12px',
          borderBottom: '1px solid #222222',
          borderRight: '1px solid #333',
          minWidth: '200px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <LabelEditor visitorHash={visitor.visitorHash} currentLabel={visitor.label} />
          <Link
            to={`/reviewer/analytics/visitor/${encodeURIComponent(visitor.visitorHash)}`}
            style={{ color: '#666', fontSize: '12px', marginLeft: 'auto', flexShrink: 0 }}
            title="View visitor details"
            onClick={(e) => e.stopPropagation()}
          >
            <RightOutlined />
          </Link>
        </div>
        <div style={{ marginTop: '4px', lineHeight: '1.4' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#888' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
              {visitor.ipAddress || '-'}
            </span>
            {(visitor.city || visitor.country) && (
              <>
                <span style={{ color: '#555' }}>&middot;</span>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {[visitor.city, visitor.country].filter(Boolean).join(', ')}
                </span>
              </>
            )}
            <span style={{ color: '#555' }}>&middot;</span>
            <Tooltip title={`${visitor.device} / ${visitor.browser}`}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }}>
                {visitor.device} / {visitor.browser}
              </span>
            </Tooltip>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#888' }}>
            <Tooltip title={`${visitor.totalViews} page views, ${formatDuration(visitor.totalDuration)} total`}>
              <span style={{ color: '#d9d9d9' }}>
                {formatDuration(visitor.totalDuration)}
              </span>
            </Tooltip>
            <span style={{ color: '#555' }}>&middot;</span>
            <span>
              {dayjs(visitor.lastSeen).fromNow()}
            </span>
          </div>
        </div>
      </td>
      {columns.map((col) => {
        const page = pageMap.get(col.key);
        const views = page?.views || 0;
        const dur = page?.totalDuration || 0;

        return (
          <td
            key={col.key}
            style={{
              padding: '4px',
              borderBottom: '1px solid #222222',
              borderRight: '1px solid #222222',
              textAlign: 'center',
              background: cellColor(dur, views),
              transition: 'background 0.2s',
            }}
          >
            {views > 0 ? (
              <Tooltip
                title={
                  <div>
                    <div>{views} visit{views > 1 ? 's' : ''}</div>
                    <div>{formatDuration(dur)} total</div>
                    {page?.scrollDepth != null && <div>Scrolled: {page.scrollDepth}%</div>}
                    {page?.videoProgress != null && <div>Video: {page.videoProgress}% watched</div>}
                    {page && <div>First: {dayjs(page.firstSeen).format('MMM D, h:mm A')}</div>}
                    {page && views > 1 && <div>Last: {dayjs(page.lastSeen).format('MMM D, h:mm A')}</div>}
                  </div>
                }
              >
                <div style={{ cursor: 'default' }}>
                  <Text style={{ color: '#f5f5f5', fontSize: '12px' }}>
                    {page?.videoProgress != null ? `${page.videoProgress}%` : formatDuration(dur)}
                  </Text>
                  {views > 1 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        {views}x
                      </Text>
                    </div>
                  )}
                </div>
              </Tooltip>
            ) : null}
          </td>
        );
      })}
    </tr>
  );
}

export default VisitorJourneyMatrix;
